/**
 * Role x Sidebar Feature decision matrix.
 *
 * Single source of truth for which app surfaces each `User.role` is
 * allowed to see. Mirrors ADR-0004 §"Decision matrix" exactly. Used by:
 *
 *  - the Sidebar component for soft visibility gating (B1 — this commit).
 *  - the route guards / 403 deep-link page (D1 — later).
 *
 * Keep the two layers aligned: any change here MUST be reflected in
 * ADR-0004 and any guard logic that references the same feature key.
 *
 * NOTE: ADR-0004 lists `User.role` as one of 8 enum values. Frontend
 * receives `role` as a plain string from `/api/v1/auth/me` (lowercased
 * enum value). `role: undefined | null` is treated as "no access" — a
 * legacy user predating migration 023 sees nothing chain-related until
 * an admin assigns a role.
 */

export type Role =
  | 'super_admin'
  | 'chain_executive'
  | 'regional_manager'
  | 'store_manager'
  | 'clinical'
  | 'dispensing'
  | 'staff'
  | 'patient';

export type SidebarFeature =
  | 'dashboard'
  | 'chain.corporation'
  | 'chain.regions'
  | 'chain.districts'
  | 'chain.stores'
  | 'chain.practices'
  | 'patient_management'
  | 'inventory'
  | 'orders'
  | 'imports'
  | 'analytics'
  | 'marketing'
  | 'loyalty'
  | 'ml.ophthalmology'
  | 'iris'
  | 'knowledge'
  | 'settings'
  | 'patient_portal';

const ACCESS_MATRIX: Record<SidebarFeature, ReadonlyArray<Role>> = {
  dashboard: [
    'super_admin',
    'chain_executive',
    'regional_manager',
    'store_manager',
    'clinical',
    'dispensing',
    'staff',
  ],
  'chain.corporation': ['super_admin', 'chain_executive'],
  'chain.regions': ['super_admin', 'chain_executive', 'regional_manager'],
  'chain.districts': ['super_admin', 'chain_executive', 'regional_manager'],
  'chain.stores': [
    'super_admin',
    'chain_executive',
    'regional_manager',
    'store_manager',
  ],
  'chain.practices': [
    'super_admin',
    'chain_executive',
    'regional_manager',
    'store_manager',
  ],
  patient_management: [
    'super_admin',
    'chain_executive',
    'regional_manager',
    'store_manager',
    'clinical',
    'dispensing',
    'staff',
  ],
  // Stock is managed per practice, so chain roles (who have no single practice) are excluded.
  inventory: ['super_admin', 'store_manager', 'dispensing', 'staff'],
  // Selling: orders, payments and GST invoices. Same audience as patient management;
  // the backend scopes every order to the caller's patients regardless.
  orders: [
    'super_admin',
    'chain_executive',
    'regional_manager',
    'store_manager',
    'clinical',
    'dispensing',
    'staff',
  ],
  // Importing writes patient and stock records in bulk: store managers, clinical, admin.
  imports: ['super_admin', 'store_manager', 'clinical'],
  analytics: [
    'super_admin',
    'chain_executive',
    'regional_manager',
    'store_manager',
    'clinical',
  ],
  marketing: [
    'super_admin',
    'chain_executive',
    'regional_manager',
    'store_manager',
  ],
  loyalty: [
    'super_admin',
    'chain_executive',
    'regional_manager',
    'store_manager',
    'clinical',
    'dispensing',
    'staff',
  ],
  'ml.ophthalmology': ['super_admin', 'clinical'],
  iris: [
    'super_admin',
    'chain_executive',
    'regional_manager',
    'store_manager',
    'clinical',
    'dispensing',
    'staff',
  ],
  knowledge: [
    'super_admin',
    'chain_executive',
    'regional_manager',
    'store_manager',
    'clinical',
    'dispensing',
    'staff',
  ],
  settings: [
    'super_admin',
    'chain_executive',
    'regional_manager',
    'store_manager',
    'clinical',
    'dispensing',
    'staff',
    'patient',
  ],
  patient_portal: ['patient'],
};

/**
 * Check whether the given role can access a sidebar feature.
 *
 * Returns false for unknown / missing roles — UI is denied by default,
 * which is the safe posture for a legacy user with no role assigned.
 */
export function canAccess(
  role: Role | string | undefined | null,
  feature: SidebarFeature
): boolean {
  if (!role) return false;
  const allowed = ACCESS_MATRIX[feature];
  return allowed.includes(role as Role);
}

/**
 * Chain-level roles span practices and have no till: they may view orders and invoices in
 * their scope but not create, pay, invoice or cancel. The backend enforces the same rule
 * (403); this only decides what the UI offers.
 */
const ORDER_READ_ONLY_ROLES: ReadonlyArray<string> = ['chain_executive', 'regional_manager'];

export function canModifyOrders(
  role: Role | string | undefined | null,
  isSuperuser = false
): boolean {
  if (isSuperuser) return true;
  if (!role) return false;
  return canAccess(role, 'orders') && !ORDER_READ_ONLY_ROLES.includes(role);
}

/**
 * Roles that may sell at a price other than the item's own. Mirrors the backend's
 * `_PRICE_OVERRIDE_ROLES` (services/order_service.py), which answers 403 to everyone else who
 * tries; this only decides whether the UI offers an editable unit price.
 */
const ORDER_PRICE_OVERRIDE_ROLES: ReadonlyArray<string> = ['super_admin', 'store_manager', 'clinical'];

export function canOverrideOrderPrice(
  role: Role | string | undefined | null,
  isSuperuser = false
): boolean {
  if (isSuperuser) return true;
  if (!role) return false;
  return ORDER_PRICE_OVERRIDE_ROLES.includes(role);
}

export const ROLE_ACCESS_MATRIX = ACCESS_MATRIX;
